import {forEachReelsMatrixBySize, makeReelsMatrix} from '../../helpers';

export type ReelsSetMatrixDataProperties = {
    reelsMatrixSize: number[];
    reelsStrips: number[][];
};

export class ReelsSetMatrix {
    protected _reelsStrips: number[][] = [];
    protected _stopPositions: number[] = [];
    protected _matrix: number[][] = [];
    protected _reelsMatrixSize: number[] = [];
    protected _reelsAmount = 0;

    constructor({reelsMatrixSize, reelsStrips}: ReelsSetMatrixDataProperties) {
        this._reelsMatrixSize = reelsMatrixSize;
        this._reelsStrips = reelsStrips;
        this._stopPositions = [];
        this._matrix = makeReelsMatrix(reelsMatrixSize);
        this._reelsAmount = this._reelsMatrixSize.length;

        // setParameters stop positions
        for (let reelIndex = 0; reelIndex < this._reelsAmount; reelIndex++) {
            this._stopPositions[reelIndex] = -1;
        }
    }

    public nextSymbolInMatrix(reelIndex: number, rowIndex: number): void {
        const symbols = this.getAllSymbolsOnStrip(reelIndex);
        const reelSequence = [...this._matrix[reelIndex]];
        const oldSymbol = reelSequence[rowIndex];

        const startIndex = oldSymbol === -1 ? 0 : symbols.indexOf(oldSymbol) + 1;
        let sequence: number[] | undefined = undefined;

        symbols.push(-1);

        for (let i = startIndex; i < symbols.length; i += 1) {
            reelSequence[rowIndex] = symbols[i];
            sequence = this.findPossibleSequence(reelIndex, reelSequence);

            if (sequence) {
                break;
            }
        }

        this.setReelSequence(sequence, reelSequence, reelIndex);
    }

    public previousSymbolInMatrix(reelIndex: number, rowIndex: number): void {
        const symbols = this.getAllSymbolsOnStrip(reelIndex);
        const reelSequence = [...this._matrix[reelIndex]];
        const oldSymbol = reelSequence[rowIndex];

        if (oldSymbol === -1) {
            return;
        }
        symbols.unshift(-1);

        const startIndex = symbols.indexOf(oldSymbol) - 1;
        let sequence: number[] | undefined = undefined;

        for (let i = startIndex; i >= 0; i -= 1) {
            reelSequence[rowIndex] = symbols[i];
            sequence = this.findPossibleSequence(reelIndex, reelSequence);

            if (sequence) {
                break;
            }
        }

        this.setReelSequence(sequence, reelSequence, reelIndex);
    }

    public resetSymbolInMatrix(reelIndex: number, rowIndex: number): void {
        const reelSequence = [...this._matrix[reelIndex]];

        reelSequence[rowIndex] = -1;

        const sequence = this.findPossibleSequence(reelIndex, reelSequence);

        this.setReelSequence(sequence, reelSequence, reelIndex);
    }

    protected findPossibleSequence(reelIndex: number, reelSequence: number[]): number[] | undefined {
        const reelStrip = this._reelsStrips[reelIndex];

        // поиск последовательности
        for (let sequencePosition = 0; sequencePosition < reelStrip.length; sequencePosition += 1) {
            const sequence: number[] = [];

            reelSequence.forEach((symbolOnReel, rowIndex) => {

                const symbolPosition = (sequencePosition + rowIndex) % reelStrip.length;
                const symbolOnStrip = reelStrip[symbolPosition];

                if (symbolOnReel === -1 || symbolOnReel === symbolOnStrip) {
                    sequence.push(symbolPosition);
                }
            });

            if (sequence.length === reelSequence.length) {
                return sequence;
            }
        }

        return undefined;
    }

    protected getAllSymbolsOnStrip(reelIndex: number): number[] {
        const reelStrip = this._reelsStrips[reelIndex];
        const symbolsSet = new Set<number>();

        reelStrip.forEach(symbolsSet.add.bind(symbolsSet));

        return Array.from(symbolsSet).sort();
    }

    protected getMatrixByStopPositions(stopPositions: number[]): number[][] {
        const matrix: number[][] = [];

        forEachReelsMatrixBySize(this._reelsMatrixSize, ((reelIndex, rowIndex) => {
            if (!matrix[reelIndex]) {
                matrix[reelIndex] = [];
            }

            if (stopPositions[reelIndex] === -1) {
                matrix[reelIndex][rowIndex] = -1;
            } else {
                const strip = this._reelsStrips[reelIndex];
                const position = (stopPositions[reelIndex] + rowIndex) % strip.length;

                matrix[reelIndex][rowIndex] = strip[position];
            }
        }));

        return matrix;
    }

    protected setReelSequence(sequence: number[] | undefined, reelSequence: number[], reelIndex: number): void {
        if (sequence) {
            this._matrix[reelIndex] = reelSequence;
            this._stopPositions[reelIndex] = reelSequence.every(sym => sym === -1) ? -1 : sequence[0];
        }
    }

    public setStopPositions(stopPositions: number[]): boolean {
        const isValid = stopPositions.every((position, reelIndex) => {
            const strip = this._reelsStrips[reelIndex];

            return strip && position < strip.length && position >= -1;
        });

        if (isValid) {
            this._stopPositions = this._reelsStrips.map((strip, reelIndex) => {
                return stopPositions[reelIndex] !== undefined ? stopPositions[reelIndex] : -1;
            });
            this._matrix = this.getMatrixByStopPositions(this._stopPositions);
        }

        return isValid;
    }

    get matrix(): number[][] {
        return this._matrix;
    }

    get stopPositions(): number[] {
        return this._stopPositions;
    }
}
